import { Stack } from '@chakra-ui/react';
import { Alert } from '@time-line/components/common';
import { useShopifyProductByMerchandiseIds } from 'hooks/product/useShopifyProductByMerchandiseIds';
import { SKINCARE_PRODUCTS } from 'lib/constants/routes';
import { useRouter } from 'next/navigation';
import type { ProductPage } from 'types/cms/pages/ProductPage';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';

import type { BaseConfiguratorProps } from '../ProductConfiguratorRender';
import { Unavailable } from '../ProductConfiguratorRender/Unavailable';
import { ProductCtas } from '../ProductCtas';
import { ProductSoldOut } from '../ProductSoldOut';
import { SkincareFormatRadioGroup } from './SkincareFormatRadioGroup';

interface ProductVariationSelectorProps extends Omit<
  BaseConfiguratorProps,
  'productFormats'
> {
  slug: string;
  shopifyProduct?: ShopifyProduct | null;
  productFormats: NonNullable<ProductPage['productFormats']>;
}

export function ProductVariationSelector({
  slug,
  shopifyProduct,
  isPreorder,
  infoBox,
  ctaType,
  purchaseType,
  amazonButton,
  asin,
  isDiscountHidden,
  promoCodes,
  productFormats,
  showStickyCta,
}: ProductVariationSelectorProps) {
  const merchandiseIds = productFormats.map((format) => format.variant.gid);
  const { data: shopifyProducts, isLoading: isShopifyLoading } =
    useShopifyProductByMerchandiseIds(merchandiseIds || []);

  const router = useRouter();

  if (!shopifyProduct) {
    return <Unavailable />;
  }

  const { availableForSale, variants } = shopifyProduct;
  const otpVariant = variants[0];
  const hasMultipleOptions = !isShopifyLoading && productFormats.length > 1;

  if (!otpVariant) {
    return <Unavailable />;
  }

  return (
    <Stack spacing="2xs">
      {!availableForSale ? (
        <ProductSoldOut price={otpVariant.price} />
      ) : (
        hasMultipleOptions && (
          <SkincareFormatRadioGroup
            productFormats={productFormats}
            shopifyProducts={shopifyProducts}
            selectedSlug={slug}
            onSelect={(optionSlug) =>
              router.push(`${SKINCARE_PRODUCTS}/${optionSlug}`)
            }
            onOptionHover={(optionSlug) =>
              router.prefetch(`${SKINCARE_PRODUCTS}/${optionSlug}`)
            }
            isDiscountHidden={isDiscountHidden}
          />
        )
      )}
      {infoBox && <Alert status="info">{infoBox}</Alert>}
      <ProductCtas
        variant={otpVariant}
        otpVariant={otpVariant}
        isPreorder={isPreorder}
        ctaType={ctaType}
        purchaseType={purchaseType}
        amazonButton={amazonButton}
        asin={asin}
        isDiscountHidden={isDiscountHidden}
        promoCodes={promoCodes}
        showStickyCta={showStickyCta}
      />
    </Stack>
  );
}
